/**
 * Fotos y documentos cifrados (empresas con cifrado, docs/cifrado.md del API, etapa 3).
 *
 * El API entrega por cada archivo su llave AES-256-GCM y el vector de la foto y de la miniatura. Aquí se
 * cifra antes de subir a R2 y se descifra al ver: en R2 solo quedan bytes cifrados (cifrado + etiqueta).
 */
export interface FileEncryption {
  alg: 'A256GCM';
  key: string;
  ivMain: string;
  ivThumb: string;
}

const bytes = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

const importKey = (enc: FileEncryption, usage: KeyUsage) =>
  crypto.subtle.importKey('raw', bytes(enc.key), { name: 'AES-GCM' }, false, [usage]);

/** Cifra un archivo antes de subirlo. */
export const encryptBlob = async (blob: Blob, enc: FileEncryption, which: 'main' | 'thumb') => {
  const key = await importKey(enc, 'encrypt');
  const iv = bytes(which === 'main' ? enc.ivMain : enc.ivThumb);
  const sealed = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, await blob.arrayBuffer());
  return new Blob([sealed], { type: 'application/octet-stream' });
};

/** Descarga y descifra un archivo; devuelve un enlace local para mostrarlo (liberarlo con URL.revokeObjectURL). */
export const decryptToObjectUrl = async (url: string, enc: FileEncryption, which: 'main' | 'thumb', contentType = 'image/jpeg') => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`R2 ${res.status}`);
  const key = await importKey(enc, 'decrypt');
  const iv = bytes(which === 'main' ? enc.ivMain : enc.ivThumb);
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, await res.arrayBuffer());
  return URL.createObjectURL(new Blob([plain], { type: which === 'thumb' ? 'image/jpeg' : contentType }));
};
